<script type="module">
import axios from 'axios'
import { format } from 'date-fns/format'
import { addDays } from 'date-fns/addDays'
import { startOfTomorrow } from 'date-fns/startOfTomorrow'
import PremiumType from '@/enums/PremiumType'
import ModalBus from '@/eventBus'
import { nextTick } from 'vue'

export default {
  name: 'PremiumForm',

  props: {
    hasCloseButton: {
      type: Boolean,
      default: true,
    },

    premiumOnly: {
      type: Boolean,
      default: true,
    },

    support: Array,
    packs: Array,
    currency: String,
    countriesList: Array,
    settingsCustom: Object,

    settingsSupport: {
      type: Object,
      required: false,
    },

    payNowPossible: Boolean,
  },

  data() {
    return {
      form: {
        token: '',
        user: {
          lastname: window.Laravel.user?.lastname,
          firstname: window.Laravel.user?.firstname,
          email: window.Laravel.user?.email,
          country: window.Laravel.user?.country,
          phone: window.Laravel.user?.phone,

          password: '',
          password_confirmation: '',
        },

        order: {
          start_at: format(startOfTomorrow(), 'yyyy-MM-dd'),
          end_at: format(addDays(startOfTomorrow(), 1), 'yyyy-MM-dd'),
          premium_type: this.premiumOnly ? PremiumType.PREMIUM : PremiumType.SUPPORT,
          count: 0,
          comment: '',
        },
      },

      isSending: false,
      later: false,
      errors: {},
      errorMessage: '',
    }
  },

  computed: {
    totalPrice() {
      if (this.form.order.premium_type === PremiumType.SUPPORT) {
        return this.support.price
      }

      return this.packs[this.form.order.count]?.price ?? 0
    },

    subtitle() {
      return this.settingsCustom?.subtitle
    },

    isPremium() {
      return this.form.order.premium_type === PremiumType.PREMIUM
    },
  },

  methods: {
    closeForm() {
      ModalBus.emit('close')
    },

    handleSubmit() {
      this.$recaptcha('support').then((token) => {
        this.form.token = token

        this.isSending = true

        axios
          .post(this.later ? '/commande?plus-tard=true' : '/commande', this.form)
          .then((response) => {
            if (response.data.url) {
              if (response.data.newTab) {
                window.open(response.data.url, '_blank').focus()
                ModalBus.emit('close')
                if (response.data.reload) {
                  window.scrollTo(0, 0)
                  window.location.reload()
                }
              } else {
                window.location.href = response.data.url
              }
            } else {
              if (response.data.success) {
                this.$root.openPayLaterPopup()
              }
            }
          })
          .catch((error) => {
            if (error.response.status === 422) {
              this.errors = error.response.data.errors || {}

              nextTick(() => {
                const el = this.$el.querySelector('.text-red-500').parentNode
                el.scrollIntoView()
              })
            } else if (error.response.status === 500) {
              this.errorMessage = error.response.data.message
            }
          })
          .finally(() => {
            this.isSending = false
            this.later = false
          })
      })
    },
  },
}
</script>

<template>
  <div class="h-auto overflow-y-auto bg-lightgray md:rounded-2xl">
    <div class="item-center flex justify-between border-b-2 border-light/25 p-5">
      <div class="flex items-center">
        <h3 class="font-poppins text-xl font-medium text-secondary">
          {{
            premiumOnly
              ? $t('premium.title', { name: $data.appName })
              : $t('support.form.title', { name: $data.appName })
          }}
        </h3>
      </div>
      <button
        v-if="hasCloseButton"
        class="relative h-10 w-10 rounded-full bg-secondary focus:outline-none"
        @click="closeForm">
        <div
          class="absolute left-1/2 top-1/2 flex h-1/2 w-1/2 -translate-x-1/2 -translate-y-1/2 flex-col items-end justify-between">
          <div class="header-closed absolute bottom-1/2 h-0.5 w-full rotate-45 rounded bg-white"></div>
          <div class="header-closed absolute bottom-1/2 h-0.5 w-full -rotate-45 rounded bg-white"></div>
        </div>
      </button>
    </div>
    <form
      method="POST"
      @submit.prevent="handleSubmit">
      <div class="p-5">
        <div
          v-if="premiumOnly"
          class="mb-4">
          <p
            class="mb-2 font-sans text-sm text-light"
            v-html="subtitle"></p>
        </div>
        <div class="flex w-full items-center">
          <user class="h-4 w-4 text-primary" />
          <h3 class="ml-4 font-poppins text-xl font-medium">
            {{ $t('commons.quoteForm.customerInfos.title') }}
          </h3>
        </div>
        <div class="w-full">
          <div class="grid grid-cols-2 gap-x-4">
            <label class="form__label">
              <span>{{ $t('commons.quoteForm.customerInfos.form.lastname.label') }}</span>
              <input
                v-model="form.user.lastname"
                :placeholder="$t('commons.quoteForm.customerInfos.form.lastname.placeholder')"
                class="form__input bg-lightgray md:bg-white"
                type="text" />
              <span
                v-if="errors && errors['user.lastname']"
                class="text-xs text-red-500"
                >{{ errors['user.lastname'][0] }}</span
              >
            </label>
            <label class="form__label">
              <span>{{ $t('commons.quoteForm.customerInfos.form.firstname.label') }}</span>
              <input
                v-model="form.user.firstname"
                :placeholder="$t('commons.quoteForm.customerInfos.form.firstname.placeholder')"
                class="form__input bg-lightgray md:bg-white"
                type="text" />
              <span
                v-if="errors && errors['user.firstname']"
                class="text-xs text-red-500"
                >{{ errors['user.firstname'][0] }}</span
              >
            </label>
          </div>
          <label class="form__label">
            <span>{{ $t('commons.quoteForm.customerInfos.form.email.label') }}</span>
            <input
              v-model="form.user.email"
              :placeholder="$t('commons.quoteForm.customerInfos.form.email.placeholder')"
              :disabled="$root.user.email"
              :class="$root.user.email ? 'bg-transparent' : 'bg-white'"
              class="form__input"
              name="email"
              type="email" />
            <span
              v-if="errors && errors['user.email']"
              class="text-xs text-red-500"
              >{{ errors['user.email'][0] }}</span
            >
          </label>
          <div class="grid grid-cols-2 gap-x-4">
            <label class="form__label">
              <span>{{ $t('commons.quoteForm.customerInfos.form.country.label') }}</span>
              <select
                v-model="form.user.country"
                class="form__input bg-lightgray md:bg-white">
                <option
                  disabled
                  :value="null">
                  Sélectionner
                </option>
                <option
                  v-for="country in countriesList"
                  :value="country.value">
                  {{ country.label }}
                </option>
              </select>
              <span
                v-if="errors && errors['user.country']"
                class="text-xs text-red-500"
                >{{ errors['user.country'][0] }}</span
              >
            </label>
            <label class="form__label">
              <span>{{ $t('commons.quoteForm.customerInfos.form.phone.label') }}</span>
              <div class="relative h-auto w-full">
                <input
                  v-model="form.user.phone"
                  :placeholder="$t('commons.quoteForm.customerInfos.form.phone.placeholder')"
                  class="form__input w-full bg-lightgray md:bg-white"
                  required />
                <span
                  v-if="errors && errors['user.phone']"
                  class="text-xs text-red-500"
                  >{{ errors['user.phone'][0] }}</span
                >
              </div>
            </label>
          </div>
        </div>
      </div>

      <div
        :class="!$root.user.email ? 'mt-4' : 'mt-0'"
        class="w-full p-5">
        <div class="flex w-full items-center">
          <svg
            class="h-4 w-4"
            fill="none"
            height="16"
            viewBox="0 0 16 16"
            width="16"
            xmlns="http://www.w3.org/2000/svg">
            <path
              d="M15 5H13V3C13 2.20435 12.6839 1.44129 12.1213 0.87868C11.5587 0.31607 10.7956 0 10 0C9.20435 0 8.44129 0.31607 7.87868 0.87868C7.31607 1.44129 7 2.20435 7 3V5H5C4.73478 5 4.48043 5.10536 4.29289 5.29289C4.10536 5.48043 4 5.73478 4 6V8H2C1.73478 8 1.48043 8.10536 1.29289 8.29289C1.10536 8.48043 1 8.73478 1 9C1 9.26522 1.10536 9.51957 1.29289 9.70711C1.48043 9.89464 1.73478 10 2 10H9V11H1C0.734784 11 0.48043 11.1054 0.292893 11.2929C0.105357 11.4804 0 11.7348 0 12C0 12.2652 0.105357 12.5196 0.292893 12.7071C0.48043 12.8946 0.734784 13 1 13H7V14H4V15C4 15.2652 4.10536 15.5196 4.29289 15.7071C4.48043 15.8946 4.73478 16 5 16H15C15.2652 16 15.5196 15.8946 15.7071 15.7071C15.8946 15.5196 16 15.2652 16 15V6C16 5.73478 15.8946 5.48043 15.7071 5.29289C15.5196 5.10536 15.2652 5 15 5ZM11 5H9V3C9 2.73478 9.10536 2.48043 9.29289 2.29289C9.48043 2.10536 9.73478 2 10 2C10.2652 2 10.5196 2.10536 10.7071 2.29289C10.8946 2.48043 11 2.73478 11 3V5Z"
              fill="#469752" />
          </svg>
          <h3 class="ml-4 font-poppins text-xl font-medium">
            {{ $t('premium.sections.order.title') }}
          </h3>
        </div>

        <div class="grid grid-cols-2 gap-x-4">
          <div class="form__label">
            <label for="start_date"
              ><span class="text-sm">{{ $t('premium.sections.order.form.start.label') }}</span></label
            >
            <input
              id="start_date"
              v-model="form.order.start_at"
              type="date"
              class="form__input" />
            <span
              v-if="errors && errors['order.start_at']"
              class="text-xs text-red-500"
              >{{ errors['order.start_at'][0] }}</span
            >
          </div>
          <div class="form__label">
            <label for="end_date"
              ><span class="text-sm">{{ $t('premium.sections.order.form.end.label') }}</span></label
            >
            <input
              id="end_date"
              v-model="form.order.end_at"
              type="date"
              class="form__input" />
            <span
              v-if="errors && errors['order.end_at']"
              class="text-xs text-red-500"
              >{{ errors['order.end_at'][0] }}</span
            >
          </div>
        </div>

        <div
          v-if="!premiumOnly"
          class="mt-12">
          <div class="flex w-full items-center">
            <h3 class="font-poppins text-xl font-medium">
              {{ $t('support.form.sections.support.choice_one', { name: $data.appName }) }}
            </h3>
          </div>
          <div class="mt-2 w-full border border-2 bg-white p-4 text-center">
            <label class="h-full w-full">
              <img
                :src="settingsSupport.basic.image"
                alt=""
                class="m-auto mb-2" />
              <input
                v-model="form.order.premium_type"
                value="SUPPORT"
                checked
                type="radio" />
              {{ `${support.label} - ${support.price} ${currency}` }}
            </label>
          </div>
          <div
            class="mt-2"
            v-html="settingsSupport.basic.text"></div>

          <div
            v-if="!premiumOnly"
            class="mt-12">
            <div class="flex w-full items-center">
              <h3 class="font-poppins text-xl font-medium">
                {{ $t('support.form.sections.support.choice_two', { name: $data.appName }) }}
              </h3>
            </div>
            <div
              class="mt-2"
              v-html="settingsSupport.premium.text_top"></div>
            <div class="mt-2 w-full border border-2 bg-white p-4 text-center">
              <label class="h-full w-full">
                <img
                  :src="settingsSupport.premium.image"
                  alt=""
                  class="m-auto mb-2" />
                <input
                  v-model="form.order.premium_type"
                  value="PREMIUM"
                  type="radio" />
                {{ `${settingsSupport.premium.label}` }}
              </label>
            </div>
          </div>
        </div>

        <div>
          <div v-show="isPremium">
            <label class="form__label">
              <span>{{ $t('premium.sections.order.form.pack.label', { name: $data.appName }) }}</span>
              <select
                v-model.number="form.order.count"
                class="form__input bg-lightgray md:bg-white">
                <option
                  disabled
                  :value="null">
                  Sélectionner
                </option>
                <option
                  v-for="({ label }, count) in packs"
                  :value="count">
                  {{ label }}
                </option>
              </select>
              <span
                v-if="errors && errors['order.pack']"
                class="text-xs text-red-500"
                >{{ errors['order.pack'][0] }}</span
              >
            </label>
            <p class="my-8 font-sans text-sm font-light text-light">
              {{ $t('premium.sections.order.description', { name: $data.appName }) }}
            </p>
          </div>
        </div>
      </div>
      <div class="mb-8 w-full px-5">
        <div class="flex items-end justify-between">
          <h3 class="text-xl font-bold text-primary">
            {{ $t('premium.sections.order.total') + ` ${totalPrice} ${currency}` }}
          </h3>
        </div>
        <textarea
          v-model="form.order.comment"
          :placeholder="$t('premium.sections.order.textarea')"
          class="mt-2 block w-full rounded-lg border border-light/25 bg-lightgray p-4 text-sm md:bg-white"
          name="comment"></textarea>
        <span
          v-if="errors && errors['order.comment']"
          class="text-xs text-red-500"
          >{{ errors['order.comment'][0] }}</span
        >
        <span class="mt-4 font-sans text-xs text-light">{{ $t('commons.quoteForm.mentions') }}</span>
      </div>

      <div
        v-if="errorMessage"
        class="mb-8 w-full px-5">
        <div class="relative rounded-lg bg-red-100 px-3 py-3 text-sm text-red-700">
          {{ errorMessage }}
        </div>
      </div>

      <div class="mb-4 px-5 pb-5">
        <div class="justify-between md:flex md:space-x-4 lg:block lg:space-x-0">
          <button
            v-if="payNowPossible"
            :disabled="isSending"
            class="btn mb-4"
            type="submit"
            @click="later = false">
            <span v-if="isSending && !later"><i class="fa fa-spinner animate-spin"></i></span>
            {{ $t('buttons.payOrder') }}
          </button>
          <button
            :disabled="isSending"
            class="btn mb-4"
            :class="{ 'border border-secondary bg-white text-secondary': payNowPossible }"
            type="submit"
            @click="later = true">
            <span v-if="isSending && later"><i class="fa fa-spinner animate-spin"></i></span>
            {{ $t('buttons.payLater', { name: $data.appName }) }}
          </button>
        </div>
        <a
          :href="settingsCustom.read_more"
          class="text-sm text-light underline"
          target="_blank"
          rel="noopener noreferrer"
          >{{ $t('premium.moreOnPremium', { name: $data.appName }) }}</a
        >
      </div>
    </form>
  </div>
</template>
